import { styled } from '@mui/material/styles';
import {
  Button, AppBar, Box, Container, TextField,
  Tooltip, Toolbar, IconButton, Chip,
  Typography, Badge, Paper, Backdrop,
  Card, CardContent, Avatar, Link,
  Rating, ListItem, ListItemButton,
  Drawer, Menu
} from '@mui/material';
import {
  Search as SearchIcon, KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowLeft as ArrowLeftIcon, Close as CloseIcon
} from '@mui/icons-material';
import SmartSearchIcon from '../../assets/SmartSearchIcon.svg';
import { colors, breakpoints } from '@headless-shell/shared-styles';
import { SdfButton } from '@waypoint/react-components';
import StyledAvatar from '../StyledAvatar';

const createTooltipVariant = (maxWidth, padding, arrowWidth, marginTop) => ({
  maxWidth,
  padding,
  arrowWidth,
  marginTop,
});

const tooltipVariants = {
  country: createTooltipVariant('15.625rem', 0, '2em', '0.5rem'),
  cart: createTooltipVariant('25rem', '1.5rem', '2em', '0.5rem'),
};

const buildTooltipStyles = (theme, variant = 'country') => {
  const config = tooltipVariants[variant] || tooltipVariants.country;
  
  return {
    '& .MuiTooltip-tooltip': {
      pointerEvents: 'auto',
      backgroundColor: colors.white.default,
      color: colors.black.dark,
      maxWidth: config.maxWidth,
      minWidth: variant === 'country' ? 'auto' : config.maxWidth,
      fontSize: theme.typography.cmp.sizes.md,
      borderRadius: '0.5rem',
      boxShadow: '0 0.25rem 1.25rem rgba(0, 0, 0, 0.15)',
      padding: config.padding,
      marginTop: config.marginTop,
    },
    '& .MuiTooltip-arrow': {
      color: colors.white.default,
      width: config.arrowWidth,
      '&::before': {
        width: '100%',
        height: '100%',
      },
    },
  };
};

const hoverWithBoth = (backgroundColor, color) => ({
  '&:hover': {
    ...(backgroundColor && { backgroundColor }),
    ...(color && { color }),
  },
});

export const LogoContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  cursor: 'pointer',
}));

export const LogoImage = styled(StyledAvatar)({
  width: '3.59rem',
  height: '2.4875rem',
  background: 'transparent',      
  border: 'none',
});

const DESKTOP_HEADER_HEIGHT = '4rem';

const navButtonBaseStyles = ({ active }) => ({
  color: colors.black.dark,
  textTransform: 'none',
  position: 'relative',
  minWidth: 'auto',
  height: '100%',
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: 0,
  ...hoverWithBoth(colors.white.default, colors.blue.accentBlue),
  '&::after': active ? {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '0.1875rem',
    backgroundColor: colors.blue.link,
    borderRadius: '0.1875rem 0.1875rem 0 0',
  } : {},
});

export const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'loggedIn',
})(({ loggedIn }) => ({
  backgroundColor: colors.white.default,
  boxShadow: '0 0.0625rem 0.25rem rgba(0, 0, 0, 0.1)',
  zIndex: 6,
  ...(loggedIn ? {} : { top: '3rem' }),
  height: DESKTOP_HEADER_HEIGHT,
  // Thin accent bar so you can visually distinguish the shell header from legacy
  // '&::after': {
  //   content: '""',
  //   position: 'absolute',
  //   bottom: 0,
  //   left: 0,
  //   right: 0,
  //   height: '0.001rem',
  //   background: 'linear-gradient(90deg, #0070d2 0%, #00c1d4 100%)',
  // },
}));

export const DesktopToolbar = styled(Toolbar)(() => ({
  minHeight: DESKTOP_HEADER_HEIGHT,
  height: DESKTOP_HEADER_HEIGHT,
  alignItems: 'stretch',
  padding: 0,
}));

export const NavButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  ...navButtonBaseStyles({ active }),
  ...theme.typography.cmp.labelMdBold,
  padding: '0 0.5rem'
}));

export const SmartSearchButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  ...navButtonBaseStyles({ active }),
  ...theme.typography.cmp.labelMdMedium,
  padding: '0 0.75rem',
  gap: '0.375rem'
}));

export const CountryOption = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'isMobile',
})(({ theme, selected, isMobile }) => ({
  padding: isMobile ? theme.spacing(2) : '0.75rem 1rem',
  borderRadius: isMobile ? '0.5rem' : 0,
  fontSize: isMobile ? theme.typography.cmp.sizes.base : theme.typography.cmp.sizes.md,
  fontWeight: theme.typography.cmp.weights.medium,
  cursor: 'pointer',
  backgroundColor: selected ? colors.blue.link : 'transparent',
  color: selected ? colors.white.default : colors.black.dark,
  marginBottom: isMobile ? theme.spacing(0.5) : 0,
  transition: isMobile ? 'all 0.2s ease-in-out' : 'none',
  '&:last-child': {
    marginBottom: 0,
  },
  ...hoverWithBoth(selected ? colors.blue.darker : (isMobile ? 'transparent' : colors.gray.light))
}));

export const NavSection = styled(Box)(({ theme }) => ({
  display: 'none',
  alignItems: 'stretch',
  gap: theme.spacing(1),
  flex: 1,
  height: '100%',
  marginLeft: theme.spacing(4),
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

export const ActionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  height: '100%',
}));

export const StyledSearchForm = styled('div')(() => ({
  display: 'none',
  [`@media (min-width: ${breakpoints.lg})`]: {
    display: 'block',
  },
}));

export const SearchIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== '$desktopOnly',
})(({ theme, active, $desktopOnly = false }) => ({
  display: 'flex',
  color: colors.black.dark,
  padding: '0.5rem',
  border: active ? `0.125rem solid ${colors.blue.darker}` : '0.125rem solid transparent',
  borderRadius: '50%',
  marginRight: theme.spacing(1),
  ...hoverWithBoth(colors.gray.main),
  ...($desktopOnly
    ? {
        display: 'none',
        [theme.breakpoints.up('md')]: {
          display: 'flex',
          marginRight: 0,
        },
        [`@media (min-width: ${breakpoints.lg})`]: {
          display: 'none',
        },
      }
    : {}),
}));

export const DesktopSearchTray = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'loggedIn',
})(({ loggedIn, theme }) => ({
  display: 'none',
  position: 'fixed',
  top: loggedIn ? '4rem' : '7rem',
  left: 0,
  right: 0,
  zIndex: 5,
  [theme.breakpoints.up('md')]: {
    display: 'block',
  },
  [`@media (min-width: ${breakpoints.lg})`]: {
    display: 'none',
  },
}));

export const HeaderContentContainer = styled(Container)(() => ({
  maxWidth: '96rem',
}));

export const DesktopSearchTrayContainer = styled(HeaderContentContainer)(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
}));

export const HeaderSearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: colors.white.default,
    borderRadius: '0.5rem',
    height: '2.5rem',
  },
  '&& .MuiOutlinedInput-notchedOutline': {
    borderWidth: '0.125rem',
    borderColor: colors.gray.border,
  },
  '& .MuiOutlinedInput-input': {
    padding: '0.5rem 1rem',
    fontSize: theme.typography.cmp.sizes.md,
    '&::placeholder': {
      color: colors.gray.secondary,
      opacity: 1,
    },
  },
  width: '12.5rem',
  [theme.breakpoints.up('md')]: {
    width: '18.75rem',
  },
}));

export const MobileSearchButton = styled(IconButton)(({ theme }) => ({
  padding: '1.125rem',
  display: 'flex',
  color: colors.gray.secondary,
  [theme.breakpoints.up('sm')]: {
    display: 'none',
  },
}));

export const CountryIconButton = styled(IconButton)(({ theme }) => ({
  color: colors.blue.link,
  fontSize: theme.typography.cmp.sizes.lg
}));

export const ContactNavButton = styled(NavButton)(({ theme }) => ({
  display: 'none',
  paddingLeft: 0,
  [theme.breakpoints.up('sm')]: {
    display: 'flex',
  },
}));

export const NotificationIconButton = styled(IconButton)(({ theme }) => ({
  color: colors.black.dark,
  padding: '1.5rem 0.5rem',
  '&:hover': {
    backgroundColor: 'transparent',
  },
}));

export const NotificationBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: colors.red.dark,
    color: colors.white.default,
    fontSize: theme.typography.cmp.sizes.sm,
    fontWeight: theme.typography.cmp.weights.bold,
    minWidth: '1.125rem',
    height: '1.125rem',
    borderRadius: '0.5625rem',
    top: '1.4rem',
    right: '0.5rem'
  },
}));

export const UserButton = styled(Button)(({ theme }) => ({
  color: colors.blue.link,
  textTransform: 'none',
  fontWeight: theme.typography.cmp.weights.bold,
  display: 'flex',
  alignItems: 'center',
  padding: '0.5rem 0',
  borderRadius: '0.25rem',
  ...hoverWithBoth(colors.white.default, colors.blue.brandBlue),
  [theme.breakpoints.between('xs', 'md')]: {
    paddingLeft: '1rem',
    paddingRight: '0',
  },  
}));

export const UserName = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.cmp.sizes.base,
  fontWeight: theme.typography.cmp.weights.bold,
  color: colors.blue.link,
  ...hoverWithBoth(null, colors.blue.brandBlue),
}));

export const UserMenuDivider = styled('div')(() => ({
  height: '0.0625rem',
  backgroundColor: colors.gray.divider,
  margin: '0.2rem 1rem 0.3rem 1rem',
}));

export const StyledStarIcon = styled(SmartSearchIcon)(({ theme }) => ({
  fontSize: theme.typography.cmp.sizes.base,
}));

export const SmartSearchText = styled(Box)(({ theme }) => ({
  fontWeight: theme.typography.cmp.weights.bold,
  fontSize: theme.typography.cmp.sizes.md,
  ml: theme.spacing(0.5),
  mr: theme.spacing(1),
}));

export const DropdownBackdrop = styled(Backdrop, {
  shouldForwardProp: (prop) => prop !== 'loggedIn',
})(({ loggedIn }) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.3)',
  zIndex: 4,
  top: loggedIn ? '4rem' : '7rem',
  height: '100%',
}));

export const DropdownContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'loggedIn',
})(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  width: '100%',
  maxHeight: '41rem',
  zIndex: 4,
  overflow: 'hidden',
  display: 'flex',
  boxShadow: 'none',
  backgroundColor: colors.white.default,
  [theme.breakpoints.between('md', 'lg')]: {
    maxHeight: '30rem'
  }
}));

export const Sidebar = styled(Box)(({ theme }) => ({
  flex: '0 0 18.5rem',
  backgroundColor: colors.white.default,
  borderRight: `0.0625rem solid ${colors.blue.sidebarBorder}`,
  overflow: 'auto',
  height: 'auto',
  [`@media (min-width: ${breakpoints.md}) and (max-width: ${breakpoints.desktopWide})`]: {
    flex: '0 0 16.5rem'
  },
}));

export const CategoryRow = styled(ListItem)(({ theme }) => ({
  padding: 0,
  marginBottom: theme.spacing(0.4),
  '&:last-child': {
    marginBottom: 0,
  },
}));

export const CategoryItem = styled(ListItemButton)(({ theme }) => ({
  position: 'relative',
  padding: '0.625rem 1rem',
  height: '2.25rem',
  justifyContent: 'space-between',

  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    width: '0.25rem',
    top: 0,
    bottom: 0,
    borderRadius: 6,
    backgroundColor: 'transparent',
    transition: 'background-color 150ms ease',
  },

  '& .MuiListItemText-primary': {
    color: colors.black.dark,
    ...theme.typography.cmp.labelLgBold,
    transition: 'color 150ms ease',
    letterSpacing: '-0.03125rem',
  },
  '&:hover:not(.Mui-selected)': {
    backgroundColor: colors.gray.shadow,
    '&::after': {
      backgroundColor: colors.blue.link,
    },
  },

  '&.Mui-selected': {
    backgroundColor: colors.gray.shadow,
    ...hoverWithBoth(colors.gray.shadow),
  },
  '&.Mui-selected::after': { backgroundColor: colors.blue.link },
}));

export const MainContent = styled(Box)(() => ({
  flex: 1,
  padding: '1rem 1.25rem',
  backgroundColor: colors.SMART_SEARCH_INDICATOR.light,
  display: 'flex',
  flexDirection: 'column',
  height: 'auto',
}));

export const ProductCard = styled(Card)(({ theme }) => ({
  height: '20rem',
  cursor: 'pointer',
  position: 'relative',
  boxShadow: '0 0.0625rem 0.125rem rgba(0, 0, 0, 0.1)',
  border: `0.0625rem solid ${colors.gray.cardBorder}`,
  borderRadius: '0.5rem',
  display: 'flex',
  flexDirection: 'column',
  transition: 'box-shadow 0.2s ease',
  '&:hover': {
    boxShadow: '0 0.125rem 0.5rem rgba(0, 0, 0, 0.1)',
  },
  [theme.breakpoints.between('md', 'lg')]: {
    height: '18rem'
  }
}));

export const ProductCardContent = styled(CardContent)(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  '&:last-child': {
    paddingBottom: '1rem',
  },
}));

export const ProductLogo = styled(Avatar)(({ theme }) => ({
  width: '5.375rem',
  height: '5.375rem',
  borderRadius: '0.5rem',
  fontSize: theme.typography.cmp.sizes.base,
  '& img': {
    objectFit: 'contain'
  }
}));

export const CategoryBadge = styled(Chip)(({ theme }) => ({
  backgroundColor: colors.gray.shadow,
  color: colors.black.dark,
  ...theme.typography.cmp.chipSm
}));

export const CategoryArrow = styled(Typography)(({ theme }) => ({
  color: colors.black.dark,
  fontSize: theme.typography.cmp.sizes.titleLg,
  fontWeight: theme.typography.cmp.weights.bold,
}));

export const ProductsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(3),
  [`@media (min-width: ${breakpoints.md}) and (max-width: ${breakpoints.desktopWide})`]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(2),
  }
}));

export const BestsellerBadge = styled(Badge)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  '& .MuiBadge-badge': {
    backgroundColor: colors.yellow.badgesBg,
    color: colors.black.badgeText,
    padding: '0.8rem 1rem 0.8rem 1.5rem',
    ...theme.typography.cmp.labelMdBold,
    borderRadius: 0,
    borderTopRightRadius: '0.5rem',
    borderBottomLeftRadius: '0.5rem',
    transform: 'none',
    clipPath: 'polygon(0 0, calc(100% - 0rem) 0, 100% 60%, 100% 100%, 1.25rem 100%)',
    boxShadow: '0.125rem 0.125rem 0.25rem rgba(0,0,0,0.2)',
  },
}));

export const CategoryHeading = styled(Typography)(({ theme }) => ({
  color: colors.black.dark,
  ...theme.typography.cmp.titleLgBold,
  lineHeight: 1.2,
  letterSpacing: '-0.0625rem'
}));

export const CategoryDescriptionText = styled(Typography)(({ theme }) => ({
  ...theme.typography.cmp.bodyLg,
  color: colors.black.dark,
}));

export const ProductName = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  lineHeight: 1.2,
  height: "3rem",
  color: colors.black.dark,
  ...theme.typography.cmp.titleMdBold,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  wordBreak: 'break-word',
}));

export const CompanyText = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  height: '1.5rem',
  ...theme.typography.cmp.bodyLg,
}));

export const ProductDescription = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  lineHeight: 1.4,
  flex: 1,
  ...theme.typography.cmp.bodyBase,
  wordBreak: 'break-word',
  maxHeight: '2.8em',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  '&&': {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    [theme.breakpoints.between('md', 'lg')]: {
      WebkitLineClamp: 1
    }
  },
  [theme.breakpoints.between('md', 'lg')]: {
    marginBottom: theme.spacing(2)
  },
}));

export const RatingValueText = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.cmp.weights.regular,
  marginRight: theme.spacing(1),
  color: colors.black.dark,
}));

export const ProductReviewsLink = styled(Link)(({ theme }) => ({
  color: colors.blue.link,
  fontWeight: theme.typography.cmp.weights.medium
}));

export const StyledRating = styled(Rating)(({ theme }) => ({
  marginRight: theme.spacing(1),
  '& .MuiRating-iconFilled': {
    color: colors.yellow.gold
  },
  '& .MuiRating-iconEmpty': {
    color: colors.yellow.main
  },
}));

export const ActionsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center'
}));

export const MobileHeaderBox = styled(Box)(({ theme }) => ({
  display: 'block',
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

export const MobileHeaderWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'loggedIn',
})(({ theme, loggedIn }) => ({
  
  position: 'fixed',
  top: loggedIn ? 0 : '3.5rem',
  [theme.breakpoints.up('sm')]: {
    top: loggedIn ? 0 : '3rem',
  },
  width: '100%',
  zIndex: 6,
  display: 'flex',
  flexDirection: 'column',
  
}));

export const MobileAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'loggedIn',
})(({}) => ({
  backgroundColor: colors.white.default,
  borderBottom: `0.0625rem solid ${colors.blue.link}`,
  boxShadow: '0 0.0625rem 0.25rem rgba(0, 0, 0, 0.1)',
  height: '4rem',
  justifyContent: 'center',
  position: 'relative',
}));

export const MobileToolbar = styled(Toolbar)(({ theme }) => ({
  minHeight: '4rem',
  padding: `0 ${theme.spacing(2)}`,
  justifyContent: 'space-between',
  [theme.breakpoints.between('xs', 'sm')]: {
    padding: `0 ${theme.spacing(1)}`,
  },
}));

export const MobileMenuIconButton = styled(IconButton)(() => ({
  color: colors.black.dark,
}));

export const MobileLogoContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  flexGrow: 1,
  marginLeft: theme.spacing(1),
  WebkitTapHighlightColor: 'transparent'
}));

const mobileNavButtonBaseStyles = ({ theme }) => ({
  backgroundColor: colors.blue.darker,
  color: colors.white.default,
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '1rem',
  borderRadius: '0',
  paddingTop: theme.spacing(1.25),
  paddingBottom: theme.spacing(1.25),
  paddingLeft: theme.spacing(3),
  ...hoverWithBoth(colors.blue.darker),
});

export const MobileNavButton = styled(Button)(({ theme}) => ({
  ...mobileNavButtonBaseStyles({ theme }),
  marginBottom: theme.spacing(0.125),
  justifyContent: 'flex-start',
}));

export const AISolutionsBadge = styled('span')(({ theme }) => ({
  fontWeight: 500,
  fontStyle: 'italic',
  color: colors.purple.light,
  marginLeft: '0.3rem',
  [theme.breakpoints.down('md')]: {
    color: colors.purple.main
  },
}));

export const MobileSubNavButton = styled(Button)(({ theme }) => ({
  ...mobileNavButtonBaseStyles({ theme }),
  marginBottom: theme.spacing(0.125),
  justifyContent: 'space-between',
  paddingRight: theme.spacing(3),
}));

export const MobileCategoryButton = styled(Button)(({ theme }) => ({
  color: colors.black.default,
  textTransform: 'none',
  fontWeight: theme.typography.cmp.weights.bold,
  fontSize: theme.typography.cmp.sizes.base,
  padding: `${theme.spacing(1.25)} ${theme.spacing(3)}`,
  justifyContent: 'space-between',
  letterSpacing: '-0.03125rem'
}));

export const MobileSubcategoryButton = styled(Button)(({ theme }) => ({
  color: colors.black.dark,
  textTransform: 'none',
  fontWeight: theme.typography.cmp.weights.regular,
  fontSize: theme.typography.cmp.sizes.base,
  padding: `${theme.spacing(1)} ${theme.spacing(2)} ${theme.spacing(1)} ${theme.spacing(6)}`,
  justifyContent: 'flex-start',
}));

export const MobileAuthContainer = styled(Box)(({ theme }) => ({
  margin: `${theme.spacing(3)} 0 ${theme.spacing(2)}`,
  padding: `0 ${theme.spacing(2)}`,
}));

export const MobileButton = styled(SdfButton)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(2),
}));

export const MobileCountryContainer = styled(Box)(({ theme }) => ({
  padding: `0 ${theme.spacing(2)}`,
  marginBottom: theme.spacing(2),
}));

export const MobileCountrySelectBox = styled(Box)(({ theme }) => ({
  backgroundColor: colors.white.default,
  borderRadius: '0.5rem',
  fontSize: '1rem',
  color: colors.black.dark,
  border: `0.0625rem solid ${colors.gray.border}`,
  height: '2.5rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  transition: 'all 0.2s ease-in-out'
}));

export const MobileDrawerPaper = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  backgroundColor: colors.white.default,
  boxShadow: '0 0.25rem 1.25rem rgba(0,0,0,0.15)',
  marginTop: '3rem',
  maxHeight: 'calc(100vh - 3rem)',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '100vw',
    width: '100vw',
  },
}));

export const MobileCountryBottomDrawer = styled(Drawer)(() => ({
  zIndex: 7,
  '& .MuiDrawer-paper': {
    borderTopLeftRadius: '1rem',
    borderTopRightRadius: '1rem',
    maxHeight: '50vh',
    backgroundColor: colors.white.default,
    boxShadow: '0 -0.25rem 1.25rem rgba(0, 0, 0, 0.15)',
  },
}));

export const MobileCountryDrawerHandle = styled(Box)(() => ({
  width: '2.5rem',
  height: '0.25rem',
  backgroundColor: colors.gray.border,
  borderRadius: '0.125rem',
  margin: '0.75rem auto',
}));

export const MobileCountryDrawerContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  paddingTop: 0,
}));

export const DesktopHeaderBox = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('md')]: {
    display: 'block',
  },
}));

export const DesktopHeaderContent = styled(Box)(() => ({
  position: 'relative',
  width: '100%'
}));

export const ResourceMenuItem = styled(Box)(({ theme, bold, hasMyApps, resourceItem }) => ({
  paddingTop: hasMyApps ? '0.75rem' : resourceItem ? '0.75rem' : '0.1rem',
  cursor: 'pointer',
  paddingBottom: '0.75rem',
  paddingLeft: '0.7rem',
  color: colors.black.dark,
  fontSize: theme.typography.cmp.sizes.base,
  fontWeight: theme.typography.cmp.weights.regular,
  lineHeight: theme.typography.cmp.lineHeights.base,
  backgroundColor: 'transparent',
  ...hoverWithBoth(colors.blue.light),
  ...(bold ? {
    fontWeight: theme.typography.cmp.weights.bold,
    fontSize: theme.typography.cmp.sizes.base
  } : {}),
}));

export const ResourceMenuDivider = styled('div')(() => ({
  height: '0.0625rem',
  width:'12.625rem',
  backgroundColor: colors.gray.divider,
}));

export const ResourcesMenu = styled(({ slotProps, ...props }) => {
  const mergedSlotProps = {
    ...slotProps,
    paper: {
      ...slotProps?.paper,
      sx: {
        minWidth: '13.625rem',
        pt: '0.75rem',
        pl: '1rem',
        pr: '1rem',
        ...(slotProps?.paper?.sx || {}),
      },
    },
  };
  return <Menu {...props} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={ { vertical: 'top', horizontal: 'right' }} slotProps={mergedSlotProps} />;
})(() => ({
  marginTop: '1.5rem',
}));

export const UserMenuArrow = styled(ArrowDownIcon)(() => ({
  width: '1.2rem',
  height: '1.2rem',
  top: '0.15rem',
}));

export const ManageSection = styled(ResourceMenuItem, {
  shouldForwardProp: (prop) => prop !== 'hasMyApps',
})(({ theme, hasMyApps = false }) => ({
  paddingLeft: '0.5rem',
  paddingTop: hasMyApps ? '1rem': '',
  paddingBottom: '1rem',
  fontWeight: theme.typography.cmp.weights.bold,
  fontSize: theme.typography.cmp.sizes.base,
  '&:hover': {
    backgroundColor: 'transparent',
    cursor: 'default',
  },
}));

export const StyledSdfButton = styled(SdfButton)(() => ({
  display: 'flex',
  alignItems: 'center',
  padding: '0.5rem',
  gap: '0.5rem',
  alignSelf: 'stretch',
}));

export const MobileCountryTypography = styled(Typography)(({ theme }) => ({
  flex: 1,
  fontSize: theme.typography.cmp.sizes.base,
  color: colors.black.dark,
}));

export const MobileSearchBar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ open }) => ({
  backgroundColor: colors.white.default,
  position: 'relative',
  height: '100%',
  zIndex: 'auto',
  display: open ? 'block' : 'none'
}));

export const MobileSearchBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  width: '100%',
  height: '4rem',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  boxSizing: 'border-box',
  minWidth: 0,

  '& sdf-search': {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    '--sdf-context-input-search-panel-layout-base-max-width': '100%',
    '--sdf-context-input-search-panel-layout-base-min-width': '100%',
    '--sdf-context-input-search-media-size-min-panel-layout-base-max-width': '100%',
    '--sdf-context-input-search-media-size-min-panel-layout-base-min-width': '100%',
  },

  '& sdf-search::part(label-container)': {
    display: 'none',
  },
}));

export const StyledArrowLeftIcon = styled(ArrowLeftIcon)(({theme}) => ({
  fontSize: theme.typography.cmp.sizes.titleXl,
  color: colors.white.default,
  display: 'block',
  flexShrink: 0,
  alignSelf: 'center',
  cursor: 'pointer',
}));

export const MobileSearchInput = styled(TextField)(() => ({
  flex: 1,
  '& .MuiOutlinedInput-root': {
    backgroundColor: colors.white.default,
    borderRadius: '0.5rem',
    height: '2.5rem',
    paddingRight: '0.75rem'
  },
  '& .MuiOutlinedInput-input': {
    padding: '0.5rem 1rem',
    fontSize: '1rem',
    '&::placeholder': {
      color: colors.gray.secondary,
      opacity: 1,
    }
  }
}));

export const StyledCloseIcon = styled(CloseIcon)(() => ({
  color: colors.blue.btn
}));

export const MobileSearchInputIcon = styled(SearchIcon)(() => ({
  color: colors.black.dark
}));

export const CartTooltipTitleBox = styled(Box)(({ theme }) => ({
  margin: `0 -1.5rem ${theme.spacing(2)} -1.5rem`,
  padding: `0 1.5rem ${theme.spacing(2)} 1.5rem`,
  borderBottom: `0.0625rem solid ${colors.gray.divider}`,
  display: 'flex',
  alignItems: 'center', 
  justifyContent: 'space-between'
}));
export const CartTooltipTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.cmp.labelMdBold,
  fontSize: theme.typography.cmp.sizes.titleLg,
  color: colors.black.dark,
}));

export const CartItemContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr auto',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  borderBottom: `0.0625rem solid ${colors.gray.divider}`,
  alignItems: 'start',
  '&:last-child': {
    marginBottom: 0,
    paddingBottom: 0,
    borderBottom: 'none',
  },
}));

export const CartItemImage = styled(Avatar)(() => ({
  width: '2.125rem',
  height: '2.125rem',
  borderRadius: '0.5rem'
}));

export const CartItemDetails = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

export const CartItemTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.cmp.labelMdBold,
  fontSize: theme.typography.cmp.sizes.base,
  color: colors.black.dark,
  lineHeight: 1.3,
}));

export const CartItemAuthor = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.cmp.sizes.md,
  color: colors.gray.secondary,
}));

export const CartItemButton = styled(SdfButton)(({ theme }) => ({
  marginTop: theme.spacing(3),
  width: '100%',
  '--sdf-context-button-layout-sm-radius-top-left': '0',
  '--sdf-context-button-layout-sm-radius-top-right': '0',
  '--sdf-context-button-layout-sm-radius-bottom-left': '0',
  '--sdf-context-button-layout-sm-radius-bottom-right': '0',
  '--sdf-context-button-layout-sm-padding-top': '0.6rem',
  '--sdf-context-button-layout-sm-padding-right': '1.25rem',
  '--sdf-context-button-layout-sm-padding-bottom': '0.5rem',
  '--sdf-context-button-layout-sm-padding-left': '1.25rem',
}));

export const CartEmptyText = styled(Typography)(({ theme }) => ({
  ...theme.typography.cmp.labelLgBold,
  padding: theme.spacing(2),
}));

export const StyledHeaderTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme, variant = 'country' }) => buildTooltipStyles(theme, variant));

export const MobileDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'isLoggedIn',
})(({ theme, isLoggedIn }) => ({
  zIndex: 5,
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
  '& .MuiDrawer-paper': {
    zIndex: 5,
    paddingTop: isLoggedIn ? theme.spacing(2) : theme.spacing(9),
  },
}));

export const TooltipContentWrapper = styled(Box)({
  position: 'relative',
  minWidth: '12.5rem',
});

export const TooltipCloseButtonBox = styled(Box)({
  position: 'absolute',
  top: 0,
  right: 0,
  zIndex: 1
});

export const TooltipCloseIconButton = styled(IconButton)(({ theme }) => ({
  color: colors.gray.close,
  opacity: 0.5,
  padding: '0.15rem',
  '& .MuiSvgIcon-root': {
    fontWeight: theme.typography.cmp.weights.bold,
    fontSize: '1.6rem',
  }
}));

export const SearchBarContainer = styled(Box)`
  width: 100%;
`;

export const getMobileInputStyles = (isAuthenticated) => ({
  top: isAuthenticated ? '4.5rem' : '8rem',
  marginLeft: '7vw',
  marginRight: '7vw',
  width: 'calc(100% - 14vw)',
  height: '0rem',
});

export const mobileOverlayStyles = {
  backgroundColor: 'transparent',
};

export const getMobilePaneStyles = (isAuthenticated) => ({
  top: isAuthenticated ? '4rem' : '7.75rem',
});

export const mobileBackContainerStyles = {
  position: 'absolute',
  top: '25%',
  zIndex: '10',
};

export const mobileListboxStyles = {
  position: 'absolute',
  top: '3rem',
  left: '0',
  width: '85vw',
  marginLeft: '11vw',
  marginRight: '4vw',
  zIndex: '5',
  border: `0.0625rem solid ${colors.gray.border}`,
  background: `var(--sdf-context-color-interactive-background-default, ${colors.white.default})`,
  borderRadius: '0.25rem',
  listStyle: 'none',
  padding: '0',
};
